/**
 * Module prefetch helpers — warm the lazy chunk and the primary data query for a
 * module *before* the user clicks its rail item, so a cold first click does not
 * block on a dynamic import + an empty-shell network round-trip.
 *
 * Infrastructure-only (lives in `lib/`): no React UI imports, so it respects the
 * one-way import boundary and can't introduce a cycle into the layout tree. It
 * references the SAME dynamic-import factories used by ModuleComponents and, for
 * data, the SAME key + queryFn the live `useObjectsByType` hook uses, so a warmed
 * chunk/cache entry is exactly the one the module reads on mount.
 *
 * Everything here is additive and fire-and-forget — a failed prefetch is
 * swallowed (`lazyWithRetry` handles the real load-time failure; the live query
 * re-fetches on mount if the cache miss persists). The synchronous click path is
 * never touched.
 */

import type { QueryClient } from '@tanstack/react-query';
import { MODULE_ALIASES } from '@/types/modules';
import { objectsByTypeQueryKey } from '@/lib/query/keys';
import { api } from '@/lib/api';

/**
 * Canonical module types that are genuinely lazy today (code-split via
 * `lazyWithRetry` in ModuleComponents). Mapped to the SAME import factory so the
 * prefetch warms the exact chunk Suspense will await. Bundled modules are absent
 * here — chunk-prefetch for them is a no-op (the code is already in the main
 * bundle).
 *
 * `kanban` and `crm` are canonical registry types in their own right (NOT
 * MODULE_ALIASES entries), so they need their own keys here even though they
 * share a chunk with `task_list` / `contact_list`. A second import() of the
 * same specifier resolves from the module cache, so the shared chunk still
 * loads at most once.
 */
const LAZY_CHUNK_FACTORIES: Record<string, () => Promise<unknown>> = {
  email: () => import('@/modules/email/EmailPanel'),
  channels: () => import('@/modules/channels/ChannelsModule'),
  task_list: () => import('@/modules/kanban/KanbanModule'),
  kanban: () => import('@/modules/kanban/KanbanModule'),
  kanban_board: () => import('@/modules/kanban/KanbanModule'),
  drive: () => import('@/modules/drive/DriveModule'),
  note_grid: () => import('@/modules/drive/docs/DocGridModule'),
  file_viewer: () => import('@/modules/drive/FileViewerModule'),
  contact_list: () => import('@/modules/contacts/ContactListModule'),
  crm: () => import('@/modules/contacts/ContactListModule'),
  client_list: () => import('@/modules/contacts/clients/ClientListModule'),
  pages: () => import('@/modules/pages/PagesContainerModule'),
  report: () => import('@/modules/pages/report/ReportModule'),
  html_viewer: () => import('@/modules/pages/artifacts/HtmlViewerModule'),
};

/** Resolve a (possibly legacy) module type string to its canonical type. */
function canonicalType(type: string): string {
  return MODULE_ALIASES[type] ?? type;
}

/** Types whose chunk has already been requested this page session (dedupe). */
const prefetchedChunks = new Set<string>();

/**
 * Kick off the dynamic import for a module's chunk if it is code-split.
 * Idempotent: fires at most once per type per page load; non-lazy/unknown types
 * are a no-op. A rejected import is swallowed.
 */
export function prefetchModuleChunk(type: string): void {
  const canonical = canonicalType(type);
  const factory = LAZY_CHUNK_FACTORIES[canonical];
  if (!factory) return;
  if (prefetchedChunks.has(canonical)) return;
  prefetchedChunks.add(canonical);
  void factory().catch(() => {
    // Allow a future retry if this attempt failed (e.g. transient network).
    prefetchedChunks.delete(canonical);
  });
}

/**
 * Per-(canonical type) recipe for warming the module's primary list query.
 *
 * Only types whose live cache key we can reproduce EXACTLY are listed — warming
 * a mismatched key is harmless but useless, so we'd rather no-op. Each recipe
 * reuses the same key builder + fetch shape as the live `useObjectsByType` call
 * so the warmed entry is the one the module reads on mount.
 *
 * Notably absent (deliberately no-op):
 *  - `event` — its live key embeds a runtime date window (startAfter/startBefore)
 *    that isn't known at hover time, so it would never match.
 *  - `task` / `place` / `note` — these read the bare `@/features/objects/hooks`
 *    (hooks.ts) hook, whose queryFn applies a non-trivial, un-exported transform
 *    (normalizing conversation/module links, etags) before storing. Warming that
 *    key with a raw response would put a wrong-shaped value in the cache that the
 *    module would then read without refetching. Reproducing the transform here
 *    would be brittle, so we leave them to fetch normally on mount. (The lazy
 *    chunk + skeleton layers still cover the perceived-latency win.)
 */
type DataRecipe = {
  /** Build the exact query key the live hook reads, given the module instance. */
  key: (moduleId: string) => readonly unknown[];
  /** Fetch fn producing the EXACT value the live hook stores for this key. */
  fetch: (token: string | null, moduleId: string) => Promise<unknown>;
};

const DATA_RECIPES: Record<string, DataRecipe> = {
  // ContactListModule → `@/features/objects/hooks/index` (queries.ts): its live
  // key comes from the shared `objectsByTypeQueryKey` builder (#5259) with the
  // module's default-scope options (`limit: 10000, includeSourceLinked: true`;
  // `source` is undefined in the default 'xavior' scope), and its queryFn is
  // exactly `api.objects.getObjectsByType(...)` — which itself transforms +
  // returns the final value. Same key + same fn here stores the identical
  // value, so the module mounts straight from the warmed cache (SWR).
  contact_list: {
    key: (moduleId) =>
      objectsByTypeQueryKey('contact', { moduleId, limit: 10000, includeSourceLinked: true }),
    fetch: (token, moduleId) =>
      api.objects.getObjectsByType(token, 'contact', { moduleId, limit: 10000, includeSourceLinked: true }),
  },
};

/**
 * Warm a module's primary list query so the module mounts showing data (SWR)
 * instead of an empty shell. No-op unless we can reproduce the live cache key
 * exactly (see DATA_RECIPES) AND we know the concrete module instance to scope
 * the moduleId-keyed query. Fire-and-forget; failures are swallowed.
 */
export function prefetchModuleData(
  queryClient: QueryClient,
  type: string,
  ctx: { token?: string | null; moduleId?: string | null },
): void {
  const moduleId = ctx.moduleId;
  if (!moduleId) return; // module-instance-scoped key needs a concrete instance
  const recipe = DATA_RECIPES[canonicalType(type)];
  if (!recipe) return;
  void queryClient
    .prefetchQuery({
      queryKey: recipe.key(moduleId),
      queryFn: () => recipe.fetch(ctx.token ?? null, moduleId),
    })
    .catch(() => {
      // Best-effort warmup; the live query will fetch on mount if this missed.
    });
}

// Exposed for testing only — lets tests assert/reset the chunk dedupe set and
// assert each canonical type points at the correct code-split chunk (#5220).
export const __prefetchInternals = {
  prefetchedChunks,
  factories: LAZY_CHUNK_FACTORIES,
  reset: () => prefetchedChunks.clear(),
};
